import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EXECUTION_COMMENT_MAX, type ExecutionCommentView } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../components/confirm-dialog.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { ApiError } from '../../lib/api.js';
import { mapApiError } from '../../lib/errors.js';
import { executionCommentsApi, executionCommentsKey } from '../../lib/execution-comments-api.js';
import { toast } from '../../lib/toast.js';

// #193: Kommentarbereich im Ausführungsfeld. Er hält fest, was dem Tester beim Testen auffällt,
// OHNE den Testfall abzuschließen — es entsteht kein Lauf, Status und Playlist bleiben unberührt
// (deshalb auch kein Invalidieren der Playlist hier).
//
// Zwei Abschnitte, getrennt per Server-Urteil (`current`), nie per Zeitvergleich im Client:
// „Aktueller Stand" (seit dem letzten Lauf, bearbeitbar) oben, „Aus vorherigen Testläufen"
// (unveränderlich) eingeklappt darunter. Nach einem Lauf lädt das Panel die Liste neu
// (execution-panel.tsx), dann wandern die Kommentare von selbst in den zweiten Abschnitt.
//
// Kommentartext ist reiner Text: nur `whitespace-pre-wrap`, nie HTML.

// Länge wie im Schema: erst trimmen, dann zählen (ExecutionCommentBodySchema).
const lengthOf = (text: string) => text.trim().length;

function isLocked(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.message.includes('comment_locked');
}

export function ExecutionComments({
  projectId,
  planId,
  testCaseId,
}: {
  projectId: string;
  planId: string;
  testCaseId: string;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const key = executionCommentsKey(projectId, planId, testCaseId);

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Muster run-history.tsx: ohne Projekt, Plan und Testfall gibt es nichts zu laden.
  const query = useQuery({
    queryKey: key,
    queryFn: () => executionCommentsApi.list(projectId, planId, testCaseId),
    enabled: Boolean(projectId) && Boolean(planId) && Boolean(testCaseId),
  });

  const reload = () => queryClient.invalidateQueries({ queryKey: key });

  // Der zentrale Mutation-Toast (query-client.ts) kennt `comment_locked` nicht und würde
  // „Etwas ist schiefgelaufen" zeigen — hier eigener Umgang (`meta.errorToast: false`).
  // 409 comment_locked: Seit dem Kommentar lief ein Lauf (z. B. in einem zweiten Fenster), er ist
  // jetzt „vorherig". Dann Liste neu laden, damit die Anzeige zur Sperre passt, und die
  // Bearbeitung/Rückfrage schließen — beides ginge ohnehin nur noch ins Leere.
  const handleError = (err: unknown) => {
    if (isLocked(err)) {
      toast.error(t('execComments.lockedError'));
      setEditingId(null);
      setDeleteId(null);
      void reload();
      return;
    }
    toast.error(mapApiError(t, err));
    // #193: Jeder 4xx-Fehler heißt „Anzeige und Serverstand passen nicht mehr" (Kommentar inzwischen
    // gelöscht, Plan geschlossen, Recht entzogen) — Liste neu laden, damit der Tester es sieht.
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) void reload();
  };

  const create = useMutation({
    mutationFn: (body: string) => executionCommentsApi.create(projectId, planId, testCaseId, body),
    meta: { errorToast: false },
    onSuccess: () => {
      setDraft('');
      void reload();
    },
    onError: handleError,
  });

  const update = useMutation({
    mutationFn: (v: { commentId: string; body: string }) =>
      executionCommentsApi.update(projectId, planId, testCaseId, v.commentId, v.body),
    meta: { errorToast: false },
    onSuccess: () => {
      setEditingId(null);
      void reload();
    },
    onError: handleError,
  });

  const remove = useMutation({
    mutationFn: (commentId: string) =>
      executionCommentsApi.remove(projectId, planId, testCaseId, commentId),
    meta: { errorToast: false },
    onSuccess: () => {
      setDeleteId(null);
      void reload();
    },
    onError: handleError,
  });

  const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'de-DE';
  const draftLength = lengthOf(draft);
  const draftTooLong = draftLength > EXECUTION_COMMENT_MAX;
  const editLength = lengthOf(editText);
  const editTooLong = editLength > EXECUTION_COMMENT_MAX;

  const renderComment = (c: ExecutionCommentView) => {
    const editing = editingId === c.comment_id;
    return (
      <li
        key={c.comment_id}
        className="space-y-1 rounded-btn border border-[var(--border)] bg-bg-subtle p-2"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-text-muted">
          <span className="font-medium text-text-body">
            {c.author?.name ?? t('execComments.unknownAuthor')}
          </span>
          <span>{new Date(c.created_at).toLocaleString(locale)}</span>
          {c.edited && <span>{t('execComments.edited')}</span>}
          {c.can_edit && !editing && (
            <span className="ml-auto flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setEditingId(c.comment_id);
                  setEditText(c.body);
                }}
                className="text-text-body underline hover:no-underline"
              >
                {t('execComments.edit')}
              </button>
              <button
                type="button"
                onClick={() => setDeleteId(c.comment_id)}
                className="text-status-fail underline hover:no-underline"
              >
                {t('execComments.delete')}
              </button>
            </span>
          )}
        </div>
        {editing ? (
          <div className="space-y-1">
            <textarea
              rows={3}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onPaste={(e) => e.stopPropagation()}
              aria-label={t('execComments.edit')}
              className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body"
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-text-muted">
                {editLength}/{EXECUTION_COMMENT_MAX}
              </span>
              {editTooLong && (
                <span role="alert" className="text-[11px] text-status-fail">
                  {t('execComments.tooLong')}
                </span>
              )}
              <span className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  className="px-2 py-1 text-xs text-text-muted hover:text-text-body"
                >
                  {t('execComments.cancelEdit')}
                </button>
                <button
                  type="button"
                  disabled={editLength === 0 || editTooLong || update.isPending}
                  onClick={() => update.mutate({ commentId: c.comment_id, body: editText.trim() })}
                  className="rounded-btn bg-brand-accent px-2 py-1 text-xs text-white disabled:opacity-60"
                >
                  {t('execComments.saveEdit')}
                </button>
              </span>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm text-text-body">{c.body}</p>
        )}
      </li>
    );
  };

  const comments = query.data?.comments ?? [];
  const current = comments.filter((c) => c.current);
  const previous = comments.filter((c) => !c.current);

  return (
    <section className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div>
        <h3 className="text-sm font-bold text-text-primary">{t('execComments.title')}</h3>
        <p className="text-xs text-text-muted">{t('execComments.hint')}</p>
      </div>

      {query.isError ? (
        <QueryErrorNotice compact onRetry={() => query.refetch()} />
      ) : !query.data ? (
        <p className="text-xs text-text-muted">{t('common.loading')}</p>
      ) : (
        <>
          <div className="space-y-1.5">
            <h4 className="text-[11px] font-bold uppercase tracking-wide text-text-muted">
              {t('execComments.current')}
            </h4>
            {current.length === 0 ? (
              <p className="text-xs text-text-muted">{t('execComments.empty')}</p>
            ) : (
              <ul className="space-y-1.5">{current.map(renderComment)}</ul>
            )}
          </div>

          {query.data.can_write && (
            <div className="space-y-1">
              <textarea
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                // #193: Das Ausführungsfeld übernimmt eingefügte Bilder als Lauf-Screenshot (onPaste
                // dort) — im Kommentarfeld soll Einfügen nur Text betreffen.
                onPaste={(e) => e.stopPropagation()}
                placeholder={t('execComments.placeholder')}
                aria-label={t('execComments.title')}
                className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-body"
              />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-text-muted">
                  {draftLength}/{EXECUTION_COMMENT_MAX}
                </span>
                {draftTooLong && (
                  <span role="alert" className="text-[11px] text-status-fail">
                    {t('execComments.tooLong')}
                  </span>
                )}
                <button
                  type="button"
                  disabled={draftLength === 0 || draftTooLong || create.isPending}
                  onClick={() => create.mutate(draft.trim())}
                  className="ml-auto rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
                >
                  {t('execComments.save')}
                </button>
              </div>
            </div>
          )}

          {previous.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-xs font-medium text-text-body">
                {t('execComments.previous')} ({previous.length})
              </summary>
              <ul className="mt-2 space-y-1.5">{previous.map(renderComment)}</ul>
            </details>
          )}
        </>
      )}

      {deleteId && (
        <ConfirmDialog
          title={t('execComments.deleteTitle')}
          message={t('execComments.deleteMessage')}
          confirmLabel={t('execComments.delete')}
          danger
          pending={remove.isPending}
          onCancel={() => setDeleteId(null)}
          onConfirm={() => remove.mutate(deleteId)}
        />
      )}
    </section>
  );
}
